import React, { useEffect } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

const boxStyles = cssMap({
	wrapperStyles: {
		boxSizing: 'border-box',
	},
});

const HOVER_OUT_TIMEOUT = 400; // ms

export type HoverOffWrapperProps = {
	callback: () => void;
	children: React.ReactNode;
	enabled?: boolean;
};

const HoverOutWrapper = ({ callback, children }: Omit<HoverOffWrapperProps, 'enabled'>) => {
	const [handleHoverChange, cancel] = useDebouncedCallback((hoveringOn: boolean) => {
		if (!hoveringOn) {
			callback();
		}
	}, HOVER_OUT_TIMEOUT);

	// We don't want callback if the component is unmounted
	useEffect(() => {
		return () => {
			cancel();
		};
	}, [cancel]);

	return (
		<Box
			onMouseEnter={() => {
				cancel(); // Cancel any pending hover-out callback
				handleHoverChange(true);
			}}
			onMouseLeave={() => handleHoverChange(false)}
			xcss={boxStyles.wrapperStyles}
		>
			{children}
		</Box>
	);
};

export const MaybeHoverOutWrapper = ({
	enabled = false,
	...rest
}: HoverOffWrapperProps): React.JSX.Element => {
	if (!enabled) {
		return <>{rest.children}</>;
	}

	return <HoverOutWrapper {...rest} />;
};
